import { serialize } from "core/editor/serializer"
import Plain from "@convertkit/slate-plain-serializer"
import { Value } from "@convertkit/slate"
import { parse } from "@convertkit/editor"

/**
 * Returns the `value_html` for a Slate element.
 *
 * @param {Object} value - the `value` from Slate
 */

export function toHTML(value) {
  const valueFromJSON = Value.fromJSON(value)
  return serialize(valueFromJSON)
}

/**
 * Returns the `value_plain` for a Slate element.
 *
 * @param {Object} value - the `value` from Slate
 */

export function toPlain(value) {
  const valueFromJSON = Value.fromJSON(value)
  return Plain.serialize(valueFromJSON)
}

/**
 * Returns the Slate `value` for a string of HTML.
 *
 * @param {String} html
 */

export function htmlToValue(html) {
  const parsed = parse(html)
  return JSON.stringify(parsed)
}

/**
 * Returns an object containing the `value`, `value_html`, and `value_plain` for
 * a Slate text element. This provides a spreadable value for assembling
 * requests.
 *
 * @param {Object} value - the `value` from Slate
 */

export function getDerivedTextValues(value) {
  return {
    value: JSON.stringify(value),
    value_html: toHTML(value),
    value_plain: toPlain(value)
  }
}

/**
 * Returns a JSON object regardless of whether the value argument is
 * a Value object or JSON object
 *
 * @param {Object} value - value
 */

export function asJSON(value) {
  return value instanceof Value ? value.toJSON() : value
}

/**
 * Combines two Slate `value`s.
 *
 * @param {Object} valueOne - first value to merge
 * @param {Object} valueTwo - second value to merge
 */

export function mergeValues(valueOne, valueTwo) {
  const htmlA = toHTML(Value.fromJSON(valueOne))
  const htmlB = toHTML(Value.fromJSON(valueTwo))

  // If there isn't any content in `valueTwo`, we don't need to merge anything.
  // #8203; is a zero-width space used by Slate which can be considered blank.
  const blankValues = ["<p></p>", "<p>&#8203;</p>"]
  const aIsBlank = blankValues.includes(htmlA)
  const bIsBlank = blankValues.includes(htmlB)

  if (aIsBlank && bIsBlank) {
    return valueOne
  } else if (aIsBlank && !bIsBlank) {
    return valueTwo
  } else if (bIsBlank && !aIsBlank) {
    return valueOne
  } else {
    // The value might be a Value object or just a JSON object
    const valueOneJson = asJSON(valueOne)
    const valueTwoJson = asJSON(valueTwo)
    valueOneJson.document.nodes = valueOneJson.document.nodes.concat(
      valueTwoJson.document.nodes
    )
    return Value.fromJSON(valueOneJson)
  }
}
